import { Head } from "@inertiajs/react";
import CtaBand from "@/components/marketing/cta-band";
import PageHero from "@/components/marketing/page-hero";
import SectionHeading from "@/components/marketing/section-heading";
import SmartLink from "@/components/marketing/smart-link";
import PublicLayout from "@/layouts/public-layout";
import { siteLinks } from "@/data/marketing-site";

const foundations = [
  {
    icon: "bi bi-diagram-3",
    title: "ISMS scope & Governance",
    copy: "Rammen der gør NIS2 til et ledelsesanliggende med klar struktur, fast styring og tydelige beslutningsveje."
  },
  {
    icon: "bi bi-journal-text",
    title: "Politikdokumentpakke",
    copy: "Et komplet policy-framework klar til tilpasning og formel godkendelse, så I ikke skal starte fra bunden."
  },
  {
    icon: "bi bi-check2-square",
    title: "Kontrolkatalog",
    copy: "Færdige kontroller med definerede formål, testmetoder og tydelig ansvarsfordeling."
  },
  {
    icon: "bi bi-folder-check",
    title: "Evidensramme",
    copy: "Konkrete eksempler på acceptable beviser, så I ved præcis, hvad der holder ved tilsyn og audit."
  },
  {
    icon: "bi bi-stars",
    title: "NIS2 AI-opslagsværk",
    copy: "Specialiseret AI-værktøj trænet i dansk NIS2-lovgivning, så komplekse spørgsmål kan besvares hurtigt."
  },
  {
    icon: "bi bi-arrow-repeat",
    title: "Automatiserede opdateringer",
    copy: "Vi holder øje med lovgivningen og leverer præcise handlingsanvisninger, når kravene ændrer sig."
  }
];

const platformHighlights = [
  {
    title: "Alt inkluderet",
    copy: "Obsero Core leveres komplet med politikker, kontroller og evidensstruktur — klar til brug fra dag ét. I behøver ikke bygge noget fra bunden eller hente indhold fra tredjeparter."
  },
  {
    title: "Garanti for fuld compliance",
    copy: "Alle politikker og kontroller er gennemarbejdet af specialister, der kender NIS2 eller DORA indefra. I får en løsning, der holder — og dokumentation, der holder til et tilsyn."
  },
  {
    title: "Helt i mål på under 10 uger",
    copy: "Fordi strukturen, logikken og leverancerne allerede er på plads, kan I tilpasse, godkende og implementere i jeres egne systemer på rekordtid — helt uden tung projektledelse eller langstrakte processer."
  }
];

const journey = [
  {
    step: "1. Scope og afgrænsning",
    copy: "Afklar om virksomheden er omfattet, og hvilke dele af organisationen, tjenesterne og systemerne der indgår i scope.",
    deliverables: ["ISMS Scope & Context"]
  },
  {
    step: "2. Governance og ledelsesforankring",
    copy: "Etabler ansvar, roller, beslutningsveje og faste styringsaktiviteter, så NIS2 bliver et reelt ledelsesanliggende.",
    deliverables: ["Governance-ramme", "RACI / rolle- og ansvarsmodel", "ISMS årshjul"]
  },
  {
    step: "3. Politikstruktur og kravramme",
    copy: "Fastlæg den overordnede politikstruktur og de styrende dokumenter, som sætter kravrammen for virksomhedens arbejde med NIS2.",
    deliverables: ["Policy framework", "Review- og godkendelsesmodel"]
  },
  {
    step: "4. Kontroller og operationel efterlevelse",
    copy: "Omsæt krav til konkrete kontroller, arbejdsgange og opfølgningsmekanismer, så efterlevelse kan drives og verificeres i praksis.",
    deliverables: ["Kontrolkatalog", "Kontroltests / valideringsguides", "Kontrolprioritering"]
  },
  {
    step: "5. Ledelsesopfølgning og styringsrytme",
    copy: "Skab en fast rytme for rapportering, review, beslutninger og opfølgning, så compliance ikke bliver personafhængig eller ad hoc.",
    deliverables: ["Ledelsesreview-pakke", "Styrings- og rapporteringspakke"]
  },
  {
    step: "6. Evidens og dokumenterbar compliance",
    copy: "Saml den dokumentation, der viser, at styring, kontroller og opfølgning faktisk er etableret og fungerer.",
    deliverables: ["Evidensliste / evidence register", "Minimumsevidens", "Acceptable beviser"]
  }
];

const resultCards = [
  {
    title: "Opnå strategisk ro",
    copy: "Ledelsen kan dokumentere aktivt ansvar og minimere personlig juridisk og økonomisk risiko — så compliance ikke længere er en bekymring, men et faktum."
  },
  {
    title: "Altid klar til audit",
    copy: "Et struktureret årshjul og en komplet evidensramme betyder, at I altid har de rette svar klar til myndigheder, kunder eller revision."
  },
  {
    title: "Byg modstandskraft i hele organisationen",
    copy: "Fra bestyrelseslokalet til den daglige drift får I et sikkerhedsniveau, der holder — også når trusselsbilledet ændrer sig."
  }
];

const operatingModelCards = [
  {
    title: "Selvbetjeningsmodel",
    copy: "Ubegrænset adgang til dokumentpakken, løbende lovopdateringer og NIS2 AI-opslagsværket til teams, der vil arbejde selvstændigt."
  },
  {
    title: "Samarbejdsmodel",
    copy: "For organisationer, der vil kombinere produktets struktur med onboarding, tilpasning og tættere involvering fra Obsero i implementeringen."
  },
  {
    title: "Specialister, når det kræver det",
    copy: "I kan trække på os til bestyrelsesundervisning, årlige review af ISMS og konkret sparring om krav, kontroller og governance."
  }
];

function Core() {
  return (
    <>
      <Head title="Obsero Core">
        <meta
          head-key="description"
          name="description"
          content="Obsero Core er det komplette compliancefundament klar til brug med politikker, kontroller, evidensramme og governance til NIS2 eller DORA."
        />
      </Head>

      <PageHero
        narrow
        size="compact"
        title="Alle passer ind i vores standard."
        copy="Du får adgang til en gennemtestet service bestående af politikker, kontroller og styringsværktøjer klar til implementering direkte i jeres egne systemer. En løsning, der garanterer jeres compliance!"
        actions={[
          { href: siteLinks.contact, label: "Kontakt os", className: "btn btn-light" }
        ]}
      />

      <section className="public-section public-section--white-surface">
        <div className="public-container">
          <div className="public-split-callout">
            <div className="public-split-callout__lead public-split-callout__lead--accented">
              <SectionHeading
                eyebrow="Core: Alt hvad I skal bruge"
                title={
                  <>
                    Alt er inkluderet.
                    <br />
                    Klar til jeres systemer.
                    <br />
                    Klar til brug.
                  </>
                }
                copy="Obsero Core leveres komplet med politikker, kontroller og evidensstruktur — bygget af specialister, der kender NIS2 eller DORA indefra. I behøver ikke bygge noget fra bunden. I tilpasser, godkender og implementerer direkte i jeres egne systemer."
              />
              <p className="public-section__copy">
                <strong>Målgruppe:</strong> Virksomheder, der skal dokumentere compliance over for ledelse, kunder eller myndigheder.
              </p>

              <div className="public-inline-links">
                <SmartLink href={siteLinks.contact} className="public-inline-link">
                  Kontakt os
                </SmartLink>
              </div>
            </div>

            <div className="public-stack-list">
              {platformHighlights.map((item, index) => (
                <article key={item.title} className="public-stack-item">
                  <div className="public-stack-item__index">{String(index + 1).padStart(2, "0")}</div>
                  <div className="public-stack-item__body">
                    <h3 className="public-stack-item__title">{item.title}</h3>
                    <p className="public-stack-item__copy">{item.copy}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="public-section public-section--shell-band">
        <div className="public-container">
          <SectionHeading
            eyebrow="3 nøgleresultater"
            title="Tre ting ændrer sig, når fundamentet er på plads."
          />

          <div className="public-pillars">
            {resultCards.map((card, index) => {
              const icons = ["bi bi-compass", "bi bi-shield-check", "bi bi-building-check"];

              return (
                <article key={card.title} className="public-pillar">
                  <div className="public-icon-chip">
                    <i className={icons[index]} aria-hidden="true" />
                  </div>
                  <div className="public-pillar__body">
                    <h3 className="public-pillar__title">{card.title}</h3>
                    <p className="public-pillar__copy">{card.copy}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="public-section public-section--white-band">
        <div className="public-container">
          <SectionHeading
            eyebrow="Hvad I får med"
            title="Obsero Core: Alle standarder inkluderet."
          />

          <div className="public-module-rail">
            {foundations.map((item, index) => (
              <article key={item.title} className="public-module-row">
                <div className="public-module-row__index">{String(index + 1).padStart(2, "0")}</div>
                <div className="public-module-row__body">
                  <h3 className="public-module-row__title">{item.title}</h3>
                  <p className="public-module-row__copy">{item.copy}</p>
                </div>
                <div className="public-icon-chip" aria-hidden="true">
                  <i className={item.icon} />
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="public-section public-section--shell-band">
        <div className="public-container">
          <SectionHeading
            eyebrow="Seks trin"
            title="Vores model etablerer, driver og dokumenterer NIS2 eller DORA i praksis."
          />

          <div className="public-journey-grid">
            {journey.map((item, index) => (
              <article key={item.step} className="public-journey-card">
                <div className="public-journey-card__badge">{index + 1}</div>
                <div className="public-journey-card__inner">
                  <h3 className="public-journey-card__title">{item.step.replace(/^\d+\.\s*/, "")}</h3>
                  <p className="public-journey-card__copy">{item.copy}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="public-section public-section--white-surface">
        <div className="public-container">
          <div className="public-split-callout">
            <div className="public-split-callout__lead">
              <SectionHeading
                eyebrow="Sådan arbejder I med Core"
                title="I bestemmer – vil I gøre det selv, eller skal vi hjælpe jer?"
                copy="Obsero Core leverer det komplette fundament: alle politikker, kontroller og evidensartefakter er gennemarbejdet og klar til brug. Det er op til jer, om I vil implementere dem selv i jeres egne systemer — eller om I vil have os tæt på undervejs."
              />
            </div>

            <div className="public-stack-list">
              {operatingModelCards.map((card, index) => (
                <article key={card.title} className="public-stack-item">
                  <div className="public-stack-item__index">{String(index + 1).padStart(2, "0")}</div>
                  <div className="public-stack-item__body">
                    <h3 className="public-stack-item__title">{card.title}</h3>
                    <p className="public-stack-item__copy">{card.copy}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CtaBand
        eyebrow="Core i praksis"
        title="Er I klar til at tage det næste skridt?"
        copy="Vi gennemgår gerne løsningen, viser de centrale artefakter og gør det tydeligt, hvordan I kommer fra lovkrav til dokumenterbar compliance i drift."
        actions={[
          { href: siteLinks.contact, label: "Kontakt os", className: "btn btn-light" }
        ]}
      />
    </>
  );
}

Core.layout = (page) => <PublicLayout>{page}</PublicLayout>;

export default Core;
